import { useQuery } from '@hooks/reactQuery';

import { getConfigurationTemplate } from '@api/configurationTemplates';

import { extractQueryParams } from '@utils/common';
import { convertToMavenirFlatTreeData } from '@utils/treeFormatter/treeFormatter';

import { IConfigurationTemplate } from '@interfaces/cmModule/configurationTemplates';
import { IId, TDefaultObjectType } from '@interfaces/common/common';
import { IOnSuccess } from '@interfaces/common/handlers';
import { IFlatMoTreeNode } from '@interfaces/components/tree';

import { EUserRoles } from '@enums/common';

import { refetchOptions } from '@constants/common';
import { CONFIGURATION_TEMPLATES } from '@constants/queryKeys';

interface IUseQueryConfigurationTemplateReturned {
	configTreeTemplate: IFlatMoTreeNode[];
	configurationTemplate: TDefaultObjectType<IConfigurationTemplate>;
	isLoadingConfigTemplate: boolean;
}

type TUseQueryConfigurationTemplate = Partial<IId & IOnSuccess<(data: IFlatMoTreeNode[], params: string[]) => void>>

const useQueryConfigurationTemplate = ({
	id,
	onSuccess,
}: TUseQueryConfigurationTemplate): IUseQueryConfigurationTemplateReturned => {
	const {
		data,
		isInitialLoading,
	} = useQuery({
		queryKey: [CONFIGURATION_TEMPLATES, { id }],
		enabled: [!!id],
		queryFn: extractQueryParams(getConfigurationTemplate),
		select: (data) => {
			const tree = data
				? convertToMavenirFlatTreeData({
					data: data ? [data.tree] : [],
					isSingleSelectMode: true,
					isDefaultNodeChecked: true,
				}) : [];

			return {
				tree,
				template: data,
			};
		},
		onSuccess: (data) => {
			if (onSuccess) {
				onSuccess(
					data?.tree || [],
					data?.template?.skippedParameters || [],
				);
			}
		},
		allowedRoles: [
			EUserRoles.networkOperatorCmModuleEditor,
			EUserRoles.networkOperatorCmModuleAdmin,
			EUserRoles.networkOperatorCmModuleViewer,
		],
		...refetchOptions,
	});

	return {
		configTreeTemplate: data?.tree || [],
		configurationTemplate: data?.template || {},
		isLoadingConfigTemplate: isInitialLoading,
	};
};

export default useQueryConfigurationTemplate;
